/**
 * @file traffic-source.ts
 * @description 범용 유입 경로 추적 유틸리티 (SSOT)
 *
 * 모든 유입 경로 추적은 이 파일에서만 관리합니다.
 * Meta Pixel, Google Analytics 등에서 이 파일의 함수를 import해서 사용합니다.
 *
 * 주요 기능:
 * 1. UTM 파라미터 기반 유입 경로 감지
 * 2. Referrer 기반 자동 감지
 * 3. fbclid 기반 메타 광고 클릭 감지
 * 4. GA4 표준 Source/Medium 구분
 *
 * 사용 방법:
 * ```typescript
 * import { getTrafficSource, getTrafficInfo } from '@/lib/utils/traffic-source';
 *
 * const trafficSource = getTrafficSource();
 * console.log(trafficSource.source); // "meta_ads" | "naver" | "direct" | ...
 * console.log(trafficSource.medium); // "cpc" | "organic" | "social" | ...
 *
 * const { fbclid, trafficSource } = getTrafficInfo();
 * ```
 *
 * @dependencies
 * - 없음 (순수 함수)
 */

/**
 * 유입 경로 정보 타입
 */
export interface TrafficSource {
  source: string;
  medium: string;
  campaign: string;
  isMeta: boolean;
  isNaver: boolean;
  isKakao: boolean;
}

/**
 * UTM 파라미터 및 유입 경로 정보 추출
 * GA4 표준 채널 그룹에 맞춰 Source와 Medium을 구분
 *
 * @returns 유입 경로 정보
 */
export function getTrafficSource(): TrafficSource {
  if (typeof window === "undefined") {
    return {
      source: "direct",
      medium: "none",
      campaign: "",
      isMeta: false,
      isNaver: false,
      isKakao: false,
    };
  }

  const params = new URLSearchParams(window.location.search);
  const utmSource = params.get("utm_source") || "";
  const utmMedium = params.get("utm_medium") || "";
  const utmCampaign = params.get("utm_campaign") || "";
  const fbclid = params.get("fbclid");

  // 1. fbclid가 있으면 메타 광고에서 온 것으로 판단 (최우선)
  if (fbclid) {
    return {
      source: "meta_ads",
      medium: "cpc", // 유료 광고
      campaign: utmCampaign || "",
      isMeta: true,
      isNaver: false,
      isKakao: false,
    };
  }

  // 2. UTM 파라미터가 있으면 우선 사용
  if (utmSource) {
    // UTM medium이 없으면 소스에 따라 기본 medium 설정
    let defaultMedium = utmMedium || "none";

    // 소스별 기본 medium 설정
    if (!utmMedium) {
      if (
        utmSource.includes("facebook") ||
        utmSource.includes("fb") ||
        utmSource.includes("meta")
      ) {
        defaultMedium = "cpc"; // 메타 광고는 유료
      } else if (utmSource.includes("naver")) {
        defaultMedium = "cpc"; // 네이버 광고는 유료
      } else if (utmSource.includes("google")) {
        defaultMedium = "cpc"; // 구글 광고는 유료
      } else if (utmSource.includes("instagram")) {
        defaultMedium = "social"; // 인스타그램 자연 유입은 소셜
      } else {
        defaultMedium = "referral"; // 기타는 추천
      }
    }

    return {
      source: utmSource.toLowerCase(),
      medium: defaultMedium.toLowerCase(),
      campaign: utmCampaign || "",
      isMeta:
        utmSource.toLowerCase().includes("facebook") ||
        utmSource.toLowerCase().includes("instagram") ||
        utmSource.toLowerCase().includes("meta"),
      isNaver: utmSource.toLowerCase().includes("naver"),
      isKakao: utmSource.toLowerCase().includes("kakao"),
    };
  }

  // 3. Referrer 기반 자동 감지 (UTM이 없는 경우)
  // 광고 도메인을 먼저 체크하여 광고 유입과 자연 유입을 구별
  const referrer = document.referrer || "";
  let detectedSource = "direct";
  let detectedMedium = "none";

  if (referrer) {
    // ===== 광고 도메인 체크 (cpc) =====
    // 광고 도메인을 먼저 체크하여 자연 유입과 구별

    // 메타/페이스북/인스타그램 광고
    if (
      referrer.includes("business.facebook.com") ||
      referrer.includes("ads.facebook.com") ||
      referrer.includes("business.instagram.com")
    ) {
      detectedSource = "meta_ads";
      detectedMedium = "cpc"; // 광고 유입
    }
    // 네이버 광고
    else if (
      referrer.includes("searchad.naver.com") ||
      referrer.includes("ad.naver.com")
    ) {
      detectedSource = "naver_ads";
      detectedMedium = "cpc"; // 광고 유입
    }
    // 구글 광고
    else if (
      referrer.includes("ads.google.com") ||
      referrer.includes("googleadservices.com")
    ) {
      detectedSource = "google_ads";
      detectedMedium = "cpc"; // 광고 유입
    }
    // 카카오 광고
    else if (
      referrer.includes("ad.kakao.com") ||
      referrer.includes("advertising.kakao.com")
    ) {
      detectedSource = "kakao_ads";
      detectedMedium = "cpc"; // 광고 유입
    }

    // ===== 자연 유입 체크 (social, organic, referral) =====
    // 광고 도메인이 아닌 경우 자연 유입으로 처리

    // 페이스북 자연 유입 (광고 도메인 제외)
    else if (
      (referrer.includes("facebook.com") ||
        referrer.includes("fb.com") ||
        referrer.includes("m.facebook.com")) &&
      !referrer.includes("business.facebook.com") &&
      !referrer.includes("ads.facebook.com")
    ) {
      detectedSource = "facebook";
      detectedMedium = "social"; // 자연 유입은 소셜
    }
    // 인스타그램 자연 유입 (광고 도메인 제외)
    else if (
      (referrer.includes("instagram.com") ||
        referrer.includes("m.instagram.com")) &&
      !referrer.includes("business.instagram.com")
    ) {
      detectedSource = "instagram";
      detectedMedium = "social";
    }
    // 스레드 자연 유입
    else if (referrer.includes("threads.net")) {
      detectedSource = "threads";
      detectedMedium = "social";
    }
    // 네이버 검색 (광고 도메인 제외)
    else if (
      (referrer.includes("search.naver.com") ||
        referrer.includes("naver.com")) &&
      !referrer.includes("searchad.naver.com") &&
      !referrer.includes("ad.naver.com")
    ) {
      detectedSource = "naver";
      detectedMedium = "organic"; // 자연 검색
    }
    // 네이버 카페
    else if (referrer.includes("cafe.naver.com")) {
      detectedSource = "naver_cafe";
      detectedMedium = "referral"; // 커뮤니티 추천
    }
    // 네이버 블로그
    else if (referrer.includes("blog.naver.com")) {
      detectedSource = "naver_blog";
      detectedMedium = "referral";
    }
    // 구글 검색 (광고 도메인 제외)
    else if (
      (referrer.includes("google.com") || referrer.includes("google.co.kr")) &&
      !referrer.includes("ads.google.com") &&
      !referrer.includes("googleadservices.com")
    ) {
      detectedSource = "google";
      detectedMedium = "organic"; // 자연 검색
    }
    // 카카오톡 (광고 도메인 제외)
    else if (
      (referrer.includes("open.kakao.com") ||
        referrer.includes("talk.kakao.com")) &&
      !referrer.includes("ad.kakao.com") &&
      !referrer.includes("advertising.kakao.com")
    ) {
      detectedSource = "kakao";
      detectedMedium = "social"; // 메신저는 소셜
    }
    // 카카오톡 채널
    else if (referrer.includes("pf.kakao.com")) {
      detectedSource = "kakao_channel";
      detectedMedium = "social";
    }
    // 기타 외부 사이트
    else {
      try {
        detectedSource = new URL(referrer).hostname.replace("www.", "");
        detectedMedium = "referral"; // 추천
      } catch {
        detectedSource = "direct";
        detectedMedium = "none";
      }
    }
  }

  return {
    source: detectedSource,
    medium: detectedMedium,
    campaign: utmCampaign,
    isMeta:
      detectedSource === "facebook" ||
      detectedSource === "instagram" ||
      detectedSource === "threads" ||
      detectedSource === "meta_ads",
    isNaver: detectedSource.includes("naver") || detectedSource === "naver_ads",
    isKakao: detectedSource.includes("kakao") || detectedSource === "kakao_ads",
  };
}

/**
 * 유입 경로 정보를 포함한 완전한 트래픽 정보 반환
 * fbclid 포함하여 메타 광고 클릭 여부도 확인
 *
 * 세션 스토리지와 URL 모두에서 fbclid를 확인하여
 * 메타 광고 클릭을 정확히 감지합니다.
 *
 * @returns 유입 경로 정보 + fbclid
 *
 * @example
 * ```typescript
 * import { getTrafficInfo } from '@/lib/utils/traffic-source';
 *
 * const { fbclid, trafficSource } = getTrafficInfo();
 * console.log(trafficSource.source); // "meta_ads" | "facebook" | "naver" | ...
 * console.log(trafficSource.medium); // "cpc" | "organic" | "social" | ...
 * console.log(fbclid); // "abc123..." | null
 * ```
 */
export function getTrafficInfo(): {
  fbclid: string | null;
  trafficSource: TrafficSource;
} {
  if (typeof window === "undefined") {
    return {
      fbclid: null,
      trafficSource: {
        source: "direct",
        medium: "none",
        campaign: "",
        isMeta: false,
        isNaver: false,
        isKakao: false,
      },
    };
  }

  // 세션 스토리지에서 fbclid 찾기 (메타 광고 클릭 추적에서 저장됨)
  let fbclid = null;
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key && key.startsWith("meta_ad_click_")) {
      fbclid = key.replace("meta_ad_click_", "");
      break;
    }
  }

  // URL에서 직접 fbclid 확인 (세션 스토리지에 없을 수도 있음)
  if (!fbclid) {
    const params = new URLSearchParams(window.location.search);
    fbclid = params.get("fbclid");
  }

  // 유입 경로 정보 가져오기
  const trafficSource = getTrafficSource();

  return { fbclid, trafficSource };
}

/**
 * GA4 표준 형식으로 Source와 Medium 변환
 * GA4 채널 그룹이 올바르게 인식하도록 표준 형식 사용
 *
 * @param trafficSource 유입 경로 정보
 * @returns GA4 표준 형식의 source와 medium
 *
 * @example
 * ```typescript
 * const { trafficSource } = getTrafficInfo();
 * const { source, medium } = toGA4StandardFormat(trafficSource);
 * // 직접 방문: { source: "(direct)", medium: "(none)" }
 * // 구글 검색: { source: "google", medium: "organic" }
 * // 메타 광고: { source: "meta_ads", medium: "cpc" }
 * ```
 */
export function toGA4StandardFormat(trafficSource: TrafficSource): {
  source: string;
  medium: string;
} {
  const { source, medium } = trafficSource;

  // 직접 방문
  if (source === "direct" && medium === "none") {
    return {
      source: "(direct)",
      medium: "(none)",
    };
  }

  // 메타 광고
  if (source === "meta_ads" && medium === "cpc") {
    return {
      source: "meta_ads",
      medium: "cpc",
    };
  }

  // 네이버 광고
  if (source === "naver_ads" && medium === "cpc") {
    return {
      source: "naver_ads",
      medium: "cpc",
    };
  }

  // 구글 광고
  if (source === "google_ads" && medium === "cpc") {
    return {
      source: "google_ads",
      medium: "cpc",
    };
  }

  // 카카오 광고
  if (source === "kakao_ads" && medium === "cpc") {
    return {
      source: "kakao_ads",
      medium: "cpc",
    };
  }

  // 페이스북 자연 유입
  if (source === "facebook" && medium === "social") {
    return {
      source: "facebook",
      medium: "social",
    };
  }

  // 인스타그램 자연 유입
  if (source === "instagram" && medium === "social") {
    return {
      source: "instagram",
      medium: "social",
    };
  }

  // 스레드 자연 유입
  if (source === "threads" && medium === "social") {
    return {
      source: "threads",
      medium: "social",
    };
  }

  // 구글 검색
  if (source === "google" && medium === "organic") {
    return {
      source: "google",
      medium: "organic",
    };
  }

  // 네이버 검색
  if (source === "naver" && medium === "organic") {
    return {
      source: "naver",
      medium: "organic",
    };
  }

  // 네이버 카페/블로그
  if (
    (source === "naver_cafe" || source === "naver_blog") &&
    medium === "referral"
  ) {
    return {
      source: source, // "naver_cafe" 또는 "naver_blog"
      medium: "referral",
    };
  }

  // 카카오
  if (
    (source === "kakao" || source === "kakao_channel") &&
    medium === "social"
  ) {
    return {
      source: source, // "kakao" 또는 "kakao_channel"
      medium: "social",
    };
  }

  // UTM 파라미터로 온 경우 (소문자 변환된 값)
  // UTM source가 있으면 그대로 사용, medium도 그대로 사용
  if (source !== "direct") {
    return {
      source: source,
      medium: medium,
    };
  }

  // 기본값: 직접 방문
  return {
    source: "(direct)",
    medium: "(none)",
  };
}
